import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  ClipboardPen,
  Copy,
  Download,
  Info,
  RefreshCw,
  RotateCcw,
  Settings2,
  Square,
  Trash2,
  X,
} from "lucide-react";
import infoMarkdown from "bundle-text:./INFO.md";
import {
  ActionIcon,
  Autocomplete,
  Button,
  Checkbox,
  Drawer,
  MantineProvider,
  Modal,
  NumberInput,
  Select,
  Switch,
  Tabs,
  Table,
  createTheme,
} from "@mantine/core";
import "@mantine/core/styles.css";
import * as patternFiles from "./beeps/*.json";
import {
  getTimerTime,
  getTimingDiagnostics,
  scheduleCountdown,
  stopScheduledAudio,
} from "./audio.js";
import {
  advanceTimer,
  createTimer,
  formatDiff,
  formatLapTime,
  getDisplay,
  getPursuitDelta,
  getPursuitPace,
  recordLap,
  resetTimer,
  startCountdown,
  stopTimer,
  updateSettings,
} from "./timer.js";
import {
  deleteRide,
  filterRides,
  getSavedRiderNames,
  getSavedRides,
  MAX_RIDE_NOTES_LENGTH,
  ridesToCsv,
  saveRide,
  stripHtmlTags,
  updateRideNotes,
} from "./rides.js";
import "./styles/main.scss";

const SETTINGS_KEY = "velotimer-settings";
const BUILD_EPOCH_SECONDS = process.env.BUILD_EPOCH_SECONDS || "development";
const showTimingDiagnostics = new URLSearchParams(window.location.search).has(
  "debug",
);
const beepPatterns = Object.fromEntries(
  Object.entries(patternFiles).map(([id, pattern]) => [
    id,
    pattern.default ?? pattern,
  ]),
);
const beepPatternOptions = Object.entries(beepPatterns)
  .map(([value, pattern]) => ({ value, label: pattern.name }))
  .sort((left, right) => {
    if (left.value === "standard") return -1;
    if (right.value === "standard") return 1;
    return left.label.localeCompare(right.label);
  });

function renderInfoInline(text, keyPrefix) {
  return text
    .split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (
        (part.startsWith("*") && part.endsWith("*")) ||
        (part.startsWith("_") && part.endsWith("_"))
      ) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code className="info-inline-code" key={key}>
            {part.slice(1, -1)}
          </code>
        );
      }
      return part;
    });
}

function InfoContent() {
  const blocks = infoMarkdown
    .trim()
    .split(/\n\s*\n/)
    .filter(Boolean);

  return blocks.map((block, blockIndex) => {
    const heading = /^(#{1,6})\s+(.+)$/.exec(block);
    if (heading) {
      return (
        <h2 key={blockIndex}>
          {renderInfoInline(heading[2], `heading-${blockIndex}`)}
        </h2>
      );
    }

    const lines = block.split(/\r?\n/);
    if (lines.every((line) => /^\s*[-*]\s+/.test(line))) {
      return (
        <ul key={blockIndex}>
          {lines.map((line, lineIndex) => (
            <li key={lineIndex}>
              {renderInfoInline(
                line.replace(/^\s*[-*]\s+/, ""),
                `list-${blockIndex}-${lineIndex}`,
              )}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={blockIndex}>
        {renderInfoInline(lines.join(" "), `paragraph-${blockIndex}`)}
      </p>
    );
  });
}

function loadSettings() {
  try {
    const storedSettings =
      localStorage.getItem(SETTINGS_KEY) ??
      sessionStorage.getItem(SETTINGS_KEY) ??
      "{}";
    const settings = JSON.parse(storedSettings);
    return {
      ...settings,
      beepPattern: Object.hasOwn(beepPatterns, settings.beepPattern)
        ? settings.beepPattern
        : "standard",
    };
  } catch {
    return {};
  }
}

function useScreenWakeLock(active) {
  const wakeLock = useRef(null);

  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return undefined;

    let disposed = false;
    const request = async () => {
      if (
        disposed ||
        wakeLock.current ||
        document.visibilityState !== "visible"
      ) {
        return;
      }
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (disposed) {
          await sentinel.release();
          return;
        }
        wakeLock.current = sentinel;
        sentinel.addEventListener("release", () => {
          wakeLock.current = null;
        });
      } catch {
        wakeLock.current = null;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") request();
    };

    request();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      wakeLock.current?.release().catch(() => {});
      wakeLock.current = null;
    };
  }, [active]);
}

function useServiceWorkerUpdate(shouldApplyUpdate) {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const applyingUpdate = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;
    if (!("serviceWorker" in navigator)) return undefined;

    const serviceWorkerUrl = new URL("sw.js", document.baseURI);
    const scope = new URL(".", document.baseURI).pathname;
    let active = true;
    let registration;

    const announceWaitingWorker = () => {
      if (
        active &&
        registration?.waiting &&
        navigator.serviceWorker.controller
      ) {
        setWaitingWorker(registration.waiting);
      }
    };
    const onUpdateFound = () => {
      const installing = registration?.installing;
      if (!installing) return;

      const onStateChange = () => {
        if (installing.state !== "installed") return;
        announceWaitingWorker();
        installing.removeEventListener("statechange", onStateChange);
      };
      installing.addEventListener("statechange", onStateChange);
      onStateChange();
    };
    const onControllerChange = () => {
      if (applyingUpdate.current) window.location.reload();
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );
    navigator.serviceWorker
      .register(serviceWorkerUrl, { scope })
      .then((serviceWorkerRegistration) => {
        if (!active) return;
        registration = serviceWorkerRegistration;
        registration.addEventListener("updatefound", onUpdateFound);
        onUpdateFound();
        announceWaitingWorker();
      })
      .catch(() => {});

    return () => {
      active = false;
      registration?.removeEventListener("updatefound", onUpdateFound);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
    };
  }, []);

  useEffect(() => {
    if (!waitingWorker || !shouldApplyUpdate || applyingUpdate.current) return;
    applyingUpdate.current = true;
    setIsApplyingUpdate(true);
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker, shouldApplyUpdate]);

  return isApplyingUpdate;
}

function formatAnnouncement(display) {
  if (display.phase === "countdown")
    return `${display.seconds} seconds remaining`;
  if (display.phase === "ready") return `Ready, ${display.seconds} seconds`;
  const minutes = display.minutes ? `${display.minutes} minutes, ` : "";
  return `${minutes}${display.seconds}.${display.tenths} seconds`;
}

function ClockDigits({ display, fontReady }) {
  const isStopwatch = display.phase === "stopwatch";
  const hasMilliseconds = display.fraction?.length === 3;

  return (
    <div
      className={`clock-digits ${isStopwatch ? "is-stopwatch" : ""} ${hasMilliseconds ? "has-milliseconds" : ""} ${fontReady ? "is-font-ready" : ""}`}
    >
      <span className="clock-minute-group" aria-hidden="true">
        {isStopwatch && display.minutes && (
          <>
            <span className="clock-small-group clock-minute-slot">
              <span className="clock-ghost clock-small-ghost">88</span>
              <span className="clock-small-value">{display.minutes}</span>
            </span>
            <span className="clock-minute-colon">:</span>
          </>
        )}
      </span>
      <span className="clock-large-group" aria-hidden="true">
        <span className="clock-ghost">88</span>
        <span className="clock-large-value">{display.seconds}</span>
      </span>
      <span
        className="clock-small-group clock-fraction-slot"
        aria-hidden="true"
      >
        {isStopwatch && (
          <>
            <span className="clock-ghost clock-small-ghost">
              {hasMilliseconds ? ".888" : ".8"}
            </span>
            <span className="clock-small-value">
              .{hasMilliseconds ? display.fraction : display.tenths}
            </span>
          </>
        )}
      </span>
    </div>
  );
}

function LapTable({
  laps,
  scrollRef,
  visible,
  className = "",
  pursuitSettings,
}) {
  const isPursuit = Boolean(pursuitSettings);

  return (
    <div
      className={`lap-reserved ${className} ${isPursuit ? "pursuit-table" : ""}`}
    >
      {visible && (
        <section className="lap-section" aria-label="Lap times">
          <div className="lap-scroll" ref={scrollRef}>
            <Table
              className="lap-table"
              horizontalSpacing={6}
              layout="fixed"
              stickyHeader
              verticalSpacing={0}
              withColumnBorders={false}
              withRowBorders={false}
              withTableBorder={false}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th scope="col">Lap</Table.Th>
                  <Table.Th scope="col">Split</Table.Th>
                  <Table.Th scope="col">
                    {isPursuit ? "vs Lap" : "Diff"}
                  </Table.Th>
                  {isPursuit && <Table.Th scope="col">vs Plan</Table.Th>}
                  <Table.Th scope="col">Elapsed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {laps.map((lap, index) => {
                  const differenceClass =
                    lap.diffMs === null
                      ? ""
                      : lap.diffMs < 0
                        ? "faster"
                        : lap.diffMs > 0
                          ? "slower"
                          : "even";
                  const showSplit = laps.length > 1 && index > 0;
                  const planDelta = isPursuit
                    ? getPursuitDelta(lap, pursuitSettings, index + 1)
                    : null;
                  const planDeltaClass =
                    planDelta === null
                      ? ""
                      : planDelta < 0
                        ? "faster"
                        : planDelta > 0
                          ? "slower"
                          : "even";
                  return (
                    <Table.Tr
                      key={`${lap.lapNumber}-${lap.elapsedMs}-${index}`}
                      className={lap.lapNumber === "END" ? "end-row" : ""}
                    >
                      <Table.Td>{lap.lapNumber}</Table.Td>
                      <Table.Td>
                        {showSplit ? formatLapTime(lap.splitMs) : ""}
                      </Table.Td>
                      <Table.Td className={differenceClass}>
                        {lap.diffMs === null ? "" : formatDiff(lap.diffMs)}
                      </Table.Td>
                      {isPursuit && (
                        <Table.Td className={planDeltaClass}>
                          {planDelta === null ? "" : formatDiff(planDelta)}
                        </Table.Td>
                      )}
                      <Table.Td>{formatLapTime(lap.elapsedMs)}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        </section>
      )}
    </div>
  );
}

function formatRideDate(completedAt) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(completedAt));
}

function formatRideDateCompact(completedAt) {
  return new Intl.DateTimeFormat(undefined, {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  }).format(new Date(completedAt));
}

function formatDistance(distanceMeters) {
  return new Intl.NumberFormat().format(distanceMeters);
}

function RideComparisonTable({ rides, mode }) {
  const [leftRide, rightRide] = rides;
  const lapNumbers = [
    ...new Set(rides.flatMap((ride) => ride.laps.map((lap) => lap.lapNumber))),
  ].sort((left, right) => {
    if (left === "END") return 1;
    if (right === "END") return -1;
    return left - right;
  });
  const comparisonRows = lapNumbers.map((lapNumber) => {
    const leftLap = leftRide.laps.find((item) => item.lapNumber === lapNumber);
    const rightLap = rightRide.laps.find(
      (item) => item.lapNumber === lapNumber,
    );
    const leftValue = leftLap
      ? mode === "overall"
        ? leftLap.elapsedMs
        : leftLap.splitMs
      : null;
    const rightValue = rightLap
      ? mode === "overall"
        ? rightLap.elapsedMs
        : rightLap.splitMs
      : null;
    const difference =
      leftValue === null || rightValue === null ? null : leftValue - rightValue;

    return { lapNumber, leftValue, rightValue, difference };
  });
  const maxAbsoluteGap = Math.max(
    0,
    ...comparisonRows.map(({ difference }) => Math.abs(difference ?? 0)),
  );

  return (
    <div className="history-compare-scroll">
      <Table
        className="history-compare-table"
        layout="fixed"
        stickyHeader
        verticalSpacing={0}
        withColumnBorders={false}
        withRowBorders={false}
        withTableBorder={false}
      >
        <colgroup>
          <col className="history-compare-lap-column" />
          <col />
          <col />
          <col />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th scope="col">Lap</Table.Th>
            <Table.Th scope="col">
              <span className="history-compare-rider">
                {leftRide.riderName}
              </span>
              <span className="history-compare-context">
                {formatRideDateCompact(leftRide.completedAt)}
              </span>
              <strong>{formatLapTime(leftRide.elapsedMs)}</strong>
            </Table.Th>
            <Table.Th className="history-compare-gap-heading" scope="col">
              Gap
            </Table.Th>
            <Table.Th scope="col">
              <span className="history-compare-rider">
                {rightRide.riderName}
              </span>
              <span className="history-compare-context">
                {formatRideDateCompact(rightRide.completedAt)}
              </span>
              <strong>{formatLapTime(rightRide.elapsedMs)}</strong>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {comparisonRows.map(
            ({ lapNumber, leftValue, rightValue, difference }) => {
              const leftIsFaster = difference < 0;
              const rightIsFaster = difference > 0;
              const fasterRider = leftIsFaster ? leftRide : rightRide;
              const barWidth = maxAbsoluteGap
                ? (Math.abs(difference ?? 0) / maxAbsoluteGap) * 50
                : 0;
              const patternId = `history-compare-bars-${mode}-${lapNumber}`;

              return (
                <Table.Tr key={lapNumber}>
                  <Table.Th scope="row">
                    <svg
                      aria-hidden="true"
                      className="history-compare-row-bar"
                      focusable="false"
                    >
                      <defs>
                        <pattern
                          id={patternId}
                          height="8"
                          patternTransform="rotate(45)"
                          patternUnits="userSpaceOnUse"
                          width="8"
                        >
                          <rect width="8" height="8" fill="#363530" />
                          <path
                            d="M0 0V8"
                            fill="none"
                            stroke="#49443b"
                            strokeWidth="2"
                          />
                        </pattern>
                      </defs>
                      {barWidth > 0 && (
                        <rect
                          fill={`url(#${patternId})`}
                          height="100%"
                          width={`${barWidth}%`}
                          x={`${difference < 0 ? 50 - barWidth : 50}%`}
                          y="0"
                        />
                      )}
                    </svg>
                    {lapNumber}
                  </Table.Th>
                  <Table.Td className="history-compare-split">
                    {leftValue === null ? "—" : formatLapTime(leftValue)}
                  </Table.Td>
                  <Table.Td
                    aria-label={
                      difference === null
                        ? "No comparison available"
                        : difference === 0
                          ? "Tied at zero seconds"
                          : `${fasterRider.riderName} ${mode === "overall" ? "leads by" : "is faster on this split by"} ${(Math.abs(difference) / 1000).toFixed(3)} seconds`
                    }
                    className="history-compare-gap"
                  >
                    {difference === null ? (
                      "—"
                    ) : difference === 0 ? (
                      <span className="history-compare-even">0.000</span>
                    ) : (
                      <div className="history-compare-lead">
                        <span
                          className={`history-compare-arrow ${leftIsFaster ? "is-faster" : "is-slower"}`}
                          aria-hidden="true"
                        >
                          <ArrowLeft />
                        </span>
                        <strong>
                          {(Math.abs(difference) / 1000).toFixed(3)}
                        </strong>
                        <span
                          className={`history-compare-arrow ${rightIsFaster ? "is-faster" : "is-slower"}`}
                          aria-hidden="true"
                        >
                          <ArrowRight />
                        </span>
                      </div>
                    )}
                  </Table.Td>
                  <Table.Td className="history-compare-split">
                    {rightValue === null ? "—" : formatLapTime(rightValue)}
                  </Table.Td>
                </Table.Tr>
              );
            },
          )}
        </Table.Tbody>
      </Table>
    </div>
  );
}

function RideComparisonTabs({ rides }) {
  return (
    <Tabs
      classNames={{
        root: "history-compare-tabs",
        list: "history-compare-tabs-list",
        tab: "history-compare-tab",
        panel: "history-compare-tab-panel",
      }}
      defaultValue="overall"
    >
      <Tabs.List grow>
        <Tabs.Tab value="overall">Overall</Tabs.Tab>
        <Tabs.Tab value="splits">Splits</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel keepMounted={false} value="overall">
        <RideComparisonTable mode="overall" rides={rides} />
      </Tabs.Panel>
      <Tabs.Panel keepMounted={false} value="splits">
        <RideComparisonTable mode="splits" rides={rides} />
      </Tabs.Panel>
    </Tabs>
  );
}

function SavedRidesDrawer({
  rides,
  selectedRide,
  onSelect,
  onBack,
  onDelete,
  onSaveNotes,
  error,
  open,
  onClose,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRideIds, setSelectedRideIds] = useState([]);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [riderFilter, setRiderFilter] = useState(null);
  const [distanceFilter, setDistanceFilter] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    setNotesDraft(stripHtmlTags(selectedRide?.notes ?? ""));
  }, [selectedRide?.id, selectedRide?.notes]);

  const saveSelectedNotes = () => {
    if (!selectedRide) return true;
    return onSaveNotes(selectedRide.id, notesDraft);
  };
  const closeDrawer = () => {
    saveSelectedNotes();
    setComparisonOpen(false);
    setSelectedRideIds([]);
    onClose();
  };
  const riderOptions = [
    ...new Map(
      rides.map((ride) => [
        ride.riderName.trim().toLocaleLowerCase(),
        ride.riderName.trim(),
      ]),
    ).values(),
  ].sort((left, right) => left.localeCompare(right));
  const distanceOptions = [...new Set(rides.map((ride) => ride.distanceMeters))]
    .sort((left, right) => left - right)
    .map((distance) => ({
      value: String(distance),
      label: formatDistance(distance),
    }));
  const visibleRides = filterRides(rides, {
    riderName: riderFilter,
    distanceMeters: distanceFilter === null ? null : Number(distanceFilter),
  });
  const comparisonRides = rides.filter((ride) =>
    selectedRideIds.includes(ride.id),
  );
  const toggleRideSelection = (rideId) => {
    setSelectedRideIds((current) =>
      current.includes(rideId)
        ? current.filter((id) => id !== rideId)
        : [...current, rideId],
    );
  };

  const exportRideHistory = () => {
    const csv = ridesToCsv(rides);
    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `velotimer-rides-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
  };

  return (
    <>
      <Drawer
        classNames={{
          content: "history-drawer-content",
          header: "history-drawer-header",
          title: "history-drawer-title",
          body: "history-drawer-body",
        }}
        closeButtonProps={{
          "aria-label": "Close ride history",
          icon: <X aria-hidden="true" />,
        }}
        onClose={closeDrawer}
        opened={open}
        overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
        position="right"
        size="min(94vw, 480px)"
        title={
          selectedRide ? (
            selectedRide.riderName
          ) : comparisonOpen ? (
            "Compare rides"
          ) : (
            <>
              Ride History
              <Button
                aria-label="Export ride history as CSV"
                className="history-export-button"
                disabled={!rides.length}
                leftSection={<Download aria-hidden="true" size={16} />}
                onClick={exportRideHistory}
                size="xs"
                variant="default"
              >
                Export CSV
              </Button>
            </>
          )
        }
      >
        {error && (
          <p className="ride-form-error" role="alert">
            {error}
          </p>
        )}
        {selectedRide ? (
          <div className="history-detail">
            <Button
              className="history-back"
              leftSection={<ArrowLeft aria-hidden="true" />}
              onClick={() => {
                if (saveSelectedNotes()) onBack();
              }}
              variant="subtle"
            >
              All rides
            </Button>
            <div className="history-detail-meta">
              <span>{formatRideDate(selectedRide.completedAt)}</span>
              <span>
                {new Intl.NumberFormat().format(selectedRide.distanceMeters)} m
              </span>
              <strong>{formatLapTime(selectedRide.elapsedMs)}</strong>
            </div>
            <div className="history-notes">
              <label htmlFor="history-ride-notes">Ride notes</label>
              <textarea
                id="history-ride-notes"
                maxLength={MAX_RIDE_NOTES_LENGTH}
                rows={4}
                value={notesDraft}
                onChange={(event) =>
                  setNotesDraft(stripHtmlTags(event.currentTarget.value))
                }
              />
            </div>
            <LapTable
              className="history-lap-reserved"
              laps={selectedRide.laps}
              visible
            />
            <Button
              className="history-delete"
              leftSection={<Trash2 aria-hidden="true" />}
              onClick={() => setDeleteOpen(true)}
              variant="default"
            >
              Delete ride
            </Button>
          </div>
        ) : comparisonOpen ? (
          <div className="history-detail history-comparison-detail">
            <Button
              className="history-back"
              leftSection={<ArrowLeft aria-hidden="true" />}
              onClick={() => setComparisonOpen(false)}
              variant="subtle"
            >
              All rides
            </Button>
            <RideComparisonTabs rides={comparisonRides} />
          </div>
        ) : rides.length ? (
          <div className="history-browser">
            <div className="history-filters">
              <Select
                classNames={{
                  input: "history-filter-input",
                  label: "history-filter-label",
                }}
                clearable
                data={riderOptions}
                label="Rider"
                placeholder="All riders"
                value={riderFilter}
                onChange={setRiderFilter}
              />
              <Select
                classNames={{
                  input: "history-filter-input",
                  label: "history-filter-label",
                }}
                clearable
                data={distanceOptions}
                label="Distance"
                placeholder="All distances"
                value={distanceFilter}
                onChange={setDistanceFilter}
              />
            </div>
            {visibleRides.length ? (
              <div className="history-list-scroll">
                <div className="history-list-header" aria-hidden="true">
                  <span />
                  <span>Rider</span>
                  <span>Date</span>
                  <span>Dist</span>
                  <span>Total</span>
                </div>
                <ul className="history-list" aria-label="Saved rides">
                  {visibleRides.map((ride) => (
                    <li className="history-ride-row" key={ride.id}>
                      <Checkbox
                        aria-label={`Select ${ride.riderName}, ${formatRideDate(ride.completedAt)} for comparison`}
                        checked={selectedRideIds.includes(ride.id)}
                        className="history-ride-checkbox"
                        disabled={
                          !selectedRideIds.includes(ride.id) &&
                          selectedRideIds.length >= 2
                        }
                        onChange={() => toggleRideSelection(ride.id)}
                      />
                      <button
                        aria-label={`Open ${ride.riderName}, ${formatRideDate(ride.completedAt)}, ${formatDistance(ride.distanceMeters)} metres, ${formatLapTime(ride.elapsedMs)}`}
                        className="history-ride"
                        type="button"
                        onClick={() => onSelect(ride)}
                      >
                        <span className="history-ride-name">
                          {ride.riderName}
                        </span>
                        <time
                          dateTime={ride.completedAt}
                          title={formatRideDate(ride.completedAt)}
                        >
                          {formatRideDateCompact(ride.completedAt)}
                        </time>
                        <span className="history-ride-distance">
                          {formatDistance(ride.distanceMeters)}
                        </span>
                        <strong className="history-ride-time">
                          {formatLapTime(ride.elapsedMs)}
                        </strong>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="history-empty">No rides match these filters.</p>
            )}
            {selectedRideIds.length > 0 && (
              <div className="history-compare-footer">
                <Button
                  className="history-compare-action"
                  disabled={comparisonRides.length < 2}
                  fullWidth
                  onClick={() => setComparisonOpen(true)}
                >
                  Compare
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="history-empty">No saved rides yet.</p>
        )}
      </Drawer>

      <Modal
        centered
        classNames={{
          content: "history-confirm-content",
          header: "history-confirm-header",
          title: "history-confirm-title",
          body: "history-confirm-body",
        }}
        onClose={() => setDeleteOpen(false)}
        opened={deleteOpen}
        overlayProps={{ backgroundOpacity: 0.7, blur: 2 }}
        title="Delete this ride?"
      >
        <p className="history-confirm-copy">
          {selectedRide?.riderName} ·{" "}
          {formatLapTime(selectedRide?.elapsedMs ?? 0)}
        </p>
        <div className="history-confirm-actions">
          <Button onClick={() => setDeleteOpen(false)} variant="default">
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<Trash2 aria-hidden="true" />}
            onClick={() => {
              if (onDelete()) setDeleteOpen(false);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

function SettingsDialog({ settings, onSettingChange, open, onClose }) {
  const [firstLapDraft, setFirstLapDraft] = useState(() =>
    settings.firstLapSeconds.toFixed(1),
  );
  const [otherLapsDraft, setOtherLapsDraft] = useState(() =>
    settings.otherLapsSeconds.toFixed(1),
  );

  const changePursuitTime = (setting, setDraft, value) => {
    const draft = String(value);
    setDraft(draft);
    const seconds = Number(draft);
    if (draft !== "" && Number.isFinite(seconds) && seconds >= 0) {
      onSettingChange({ [setting]: seconds });
    }
  };
  const commitPursuitTime = (setting, setDraft, draft, savedValue) => {
    const seconds = Number(draft);
    if (draft === "" || !Number.isFinite(seconds) || seconds < 0) {
      setDraft(savedValue.toFixed(1));
      return;
    }
    const normalized = Math.round(seconds * 10) / 10;
    setDraft(normalized.toFixed(1));
    onSettingChange({ [setting]: normalized });
  };

  return (
    <Drawer
      classNames={{
        content: "settings-drawer-content",
        header: "settings-drawer-header",
        title: "settings-drawer-title",
        body: "settings-drawer-body",
      }}
      closeButtonProps={{
        "aria-label": "Close settings",
        icon: <X aria-hidden="true" />,
      }}
      onClose={onClose}
      opened={open}
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      position="right"
      size="min(90vw, 420px)"
      title="Settings"
    >
      <div className="settings-fields">
        <NumberInput
          allowDecimal={false}
          allowNegative={false}
          classNames={{
            root: "countdown-setting",
            label: "setting-label",
            input: "countdown-input",
          }}
          label="Countdown seconds"
          max={99}
          min={0}
          value={settings.countdownSeconds}
          hideControls
          clampBehavior="strict"
          onChange={(value) => {
            if (typeof value === "number" && Number.isInteger(value)) {
              onSettingChange({ countdownSeconds: value });
            }
          }}
        />
        <div className="settings-audio-options">
          <Select
            classNames={{ label: "setting-label audio-pattern-label" }}
            data={beepPatternOptions}
            label="Audio pattern"
            value={settings.beepPattern}
            allowDeselect={false}
            onChange={(beepPattern) => {
              if (beepPattern) onSettingChange({ beepPattern });
            }}
          />
          <Switch
            checked={settings.withEcho}
            className="setting-toggle"
            color="red"
            label="Echo"
            onChange={(event) =>
              onSettingChange({ withEcho: event.currentTarget.checked })
            }
          />
        </div>
        <section className="pursuit-settings" aria-labelledby="pursuit-heading">
          <div className="pursuit-settings-heading">
            <h2 id="pursuit-heading">Pursuit Mode</h2>
            <Switch
              aria-label="Pursuit Mode"
              checked={settings.pursuitMode}
              className="setting-toggle"
              color="red"
              onChange={(event) =>
                onSettingChange({ pursuitMode: event.currentTarget.checked })
              }
            />
          </div>
          <div className="pursuit-time-inputs">
            <NumberInput
              allowDecimal
              allowNegative={false}
              clampBehavior="strict"
              decimalScale={1}
              fixedDecimalScale
              disabled={!settings.pursuitMode}
              label="First lap"
              min={0}
              step={0.1}
              value={firstLapDraft}
              onChange={(value) =>
                changePursuitTime("firstLapSeconds", setFirstLapDraft, value)
              }
              onBlur={() =>
                commitPursuitTime(
                  "firstLapSeconds",
                  setFirstLapDraft,
                  firstLapDraft,
                  settings.firstLapSeconds,
                )
              }
              classNames={{
                root: "countdown-setting pursuit-time-setting",
                label: "setting-label",
                input: "countdown-input pursuit-time-input",
              }}
              hideControls
            />
            <NumberInput
              allowDecimal
              allowNegative={false}
              clampBehavior="strict"
              decimalScale={1}
              fixedDecimalScale
              disabled={!settings.pursuitMode}
              label="Other laps"
              min={0}
              step={0.1}
              value={otherLapsDraft}
              onChange={(value) =>
                changePursuitTime("otherLapsSeconds", setOtherLapsDraft, value)
              }
              onBlur={() =>
                commitPursuitTime(
                  "otherLapsSeconds",
                  setOtherLapsDraft,
                  otherLapsDraft,
                  settings.otherLapsSeconds,
                )
              }
              classNames={{
                root: "countdown-setting pursuit-time-setting",
                label: "setting-label",
                input: "countdown-input pursuit-time-input",
              }}
              hideControls
            />
          </div>
        </section>
      </div>
      <footer className="settings-credit">
        <p>
          &copy; {new Date().getFullYear()}{" "}
          <a href="https://tintou.simondickson.co.uk/">Tintou Ltd</a> &bull;{" "}
          <a href="mailto:tintou@simondickson.co.uk">Email</a> &bull;{" "}
          {BUILD_EPOCH_SECONDS}
        </p>
        <p>Always remember: the halfway point of a kilo is 900m.</p>
      </footer>
    </Drawer>
  );
}

function TimingDiagnostics({ frameMetrics, fontReady, settings }) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const sample = () =>
      setSnapshot({
        ...getTimingDiagnostics(),
        ...frameMetrics.current,
        visibility: document.visibilityState,
      });
    sample();
    const interval = setInterval(sample, 500);
    return () => clearInterval(interval);
  }, [open, frameMetrics]);

  const milliseconds = (value) =>
    Number.isFinite(value) ? `${value.toFixed(1)} ms` : "Not sampled";
  const rows = snapshot
    ? [
        ["Audio", snapshot.audioState],
        ["Clock", snapshot.clockSource],
        ["Startup wait", milliseconds(snapshot.startupDurationMs)],
        ["Output latency", milliseconds(snapshot.outputLatencyMs)],
        ["Timestamp age", milliseconds(snapshot.timestampAgeMs)],
        ["Clock correction", milliseconds(snapshot.clockOffsetMs)],
        ["Last digit lateness", milliseconds(snapshot.lastBoundaryLateMs)],
        ["Worst digit lateness", milliseconds(snapshot.maxBoundaryLateMs)],
        ["Longest frame gap", milliseconds(snapshot.maxFrameGapMs)],
        ["Worst React commit", milliseconds(snapshot.maxCommitMs)],
        ["Page", snapshot.visibility],
        ["Digit font", fontReady ? "Ready" : "Loading"],
      ]
    : [];

  const copyDiagnostics = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const report = {
      version: "audio-clock-v1",
      capturedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      visibility: document.visibilityState,
      fontReady,
      settings,
      ...getTimingDiagnostics(),
      ...frameMetrics.current,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy unavailable; take a screenshot instead.");
    }
  };

  return (
    <details
      className="timing-diagnostics"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        Timing diagnostics
        <button
          type="button"
          aria-label="Copy timing diagnostics"
          title="Copy timing diagnostics"
          onClick={copyDiagnostics}
        >
          <Copy size={16} aria-hidden="true" />
        </button>
      </summary>
      {open && (
        <div className="timing-diagnostics-panel">
          <dl>
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {snapshot?.startupError && <p>{snapshot.startupError}</p>}
          {copyStatus && <p role="status">{copyStatus}</p>}
        </div>
      )}
    </details>
  );
}

function TimerApp() {
  const [timer, setTimer] = useState(() => createTimer(loadSettings()));
  const [now, setNow] = useState(() => performance.now());
  const [displayFontReady, setDisplayFontReady] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [rides, setRides] = useState(() => getSavedRides());
  const [riderName, setRiderName] = useState("");
  const [distanceMeters, setDistanceMeters] = useState("");
  const [rideSaved, setRideSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [notice, setNotice] = useState("");
  const [pursuitLapOverlay, setPursuitLapOverlay] = useState(null);
  const [starting, setStarting] = useState(false);
  const startPending = useRef(false);
  const lapOverlaySequence = useRef(0);
  const frameMetrics = useRef({});
  const lapScrollRef = useRef(null);
  const isRunning = timer.phase === "countdown" || timer.phase === "stopwatch";
  const canAutoApplyUpdate =
    timer.phase === "ready" &&
    !starting &&
    !settingsOpen &&
    !saveOpen &&
    !historyOpen;
  const isApplyingUpdate = useServiceWorkerUpdate(canAutoApplyUpdate);
  useScreenWakeLock(isRunning);
  const lapOverlayId = pursuitLapOverlay?.id ?? null;

  useEffect(() => {
    if (lapOverlayId === null) return undefined;

    const fadeTimeout = window.setTimeout(() => {
      setPursuitLapOverlay((current) =>
        current?.id === lapOverlayId ? { ...current, fading: true } : current,
      );
    }, 2000);
    const removeTimeout = window.setTimeout(() => {
      setPursuitLapOverlay((current) =>
        current?.id === lapOverlayId ? null : current,
      );
    }, 2400);

    return () => {
      window.clearTimeout(fadeTimeout);
      window.clearTimeout(removeTimeout);
    };
  }, [lapOverlayId]);

  useEffect(() => {
    let active = true;
    const showDigits = () => {
      if (active) setDisplayFontReady(true);
    };
    const fontSet = document.fonts;

    if (typeof fontSet?.load !== "function") {
      showDigits();
      return () => {
        active = false;
      };
    }

    try {
      fontSet
        .load('italic 700 11rem "DSEG7ClassicMini"', "88")
        .then(showDigits, showDigits);
    } catch {
      showDigits();
    }

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(timer.settings));
    } catch {
      setNotice("Settings could not be saved.");
    }
  }, [timer.settings]);

  useEffect(() => {
    if (!isRunning) {
      setNow(performance.now());
      return undefined;
    }

    let frame = 0;
    let active = true;
    let previousDisplay = "";
    let previousFrameAt = null;
    let nextBoundary = null;
    const tick = () => {
      if (!active) return;
      const frameAt = performance.now();
      const sampledAt = getTimerTime(frameAt);
      if (previousFrameAt !== null) {
        frameMetrics.current.maxFrameGapMs = Math.max(
          frameMetrics.current.maxFrameGapMs ?? 0,
          frameAt - previousFrameAt,
        );
      }
      previousFrameAt = frameAt;
      const currentDisplay = getDisplay(timer, sampledAt);
      const displayKey = `${currentDisplay.phase}:${currentDisplay.minutes}:${currentDisplay.seconds}:${currentDisplay.tenths}`;
      if (displayKey !== previousDisplay) {
        previousDisplay = displayKey;
        const commitStartedAt = performance.now();
        flushSync(() => {
          setNow(sampledAt);
          setTimer((current) => advanceTimer(current, sampledAt));
        });
        const commitMs = performance.now() - commitStartedAt;
        frameMetrics.current.maxCommitMs = Math.max(
          frameMetrics.current.maxCommitMs ?? 0,
          commitMs,
        );
        if (nextBoundary !== null) {
          const lateMs = Math.max(0, sampledAt - nextBoundary) + commitMs;
          frameMetrics.current.lastBoundaryLateMs = lateMs;
          frameMetrics.current.maxBoundaryLateMs = Math.max(
            frameMetrics.current.maxBoundaryLateMs ?? 0,
            lateMs,
          );
        }
        nextBoundary =
          currentDisplay.phase === "countdown"
            ? timer.countdownDeadline -
              (Number(currentDisplay.seconds) - 1) * 1000
            : timer.countdownDeadline +
              (Math.floor((sampledAt - timer.countdownDeadline) / 100) + 1) *
                100;
      }
      if (active) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
    };
  }, [isRunning, timer]);

  useEffect(() => {
    const container = lapScrollRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [timer.laps.length]);

  useEffect(() => {
    if (!isRunning) stopScheduledAudio();
  }, [isRunning]);

  const display = getDisplay(timer, now);
  const changeSettings = (patch) => {
    setTimer((current) =>
      updateSettings(current, { ...current.settings, ...patch }),
    );
  };

  const start = async () => {
    if (startPending.current) return;
    startPending.current = true;
    setStarting(true);
    setNotice("");
    frameMetrics.current = {};
    try {
      if (timer.settings.countdownSeconds === 0) {
        stopScheduledAudio();
        const startsAt = performance.now();
        setNow(startsAt);
        setTimer((current) => startCountdown(current, startsAt));
        return;
      }

      const startsAt = await scheduleCountdown(
        timer.settings,
        beepPatterns[timer.settings.beepPattern],
      );
      setNow(getTimerTime());
      setTimer((current) => startCountdown(current, startsAt));
    } catch {
      stopScheduledAudio();
      setNotice(
        "Sound is not ready. Check audio output and press Start again.",
      );
    } finally {
      startPending.current = false;
      setStarting(false);
    }
  };

  const reset = () => {
    stopScheduledAudio();
    setNotice("");
    setRideSaved(false);
    setTimer((current) => resetTimer(current));
  };

  const openSaveDialog = () => {
    setRiderName("");
    setDistanceMeters("");
    setSaveError("");
    setSaveOpen(true);
  };

  const submitRide = (event) => {
    event.preventDefault();
    if (rideSaved || timer.phase !== "ended") return;

    try {
      saveRide({
        riderName,
        distanceMeters,
        completedAt: timer.completedAt,
        elapsedMs: timer.elapsedMs,
        laps: timer.laps,
      });
      setRides(getSavedRides());
      setRideSaved(true);
      setSaveOpen(false);
      setSaveError("");
      setNotice("");
    } catch (error) {
      setSaveError(error.message || "Ride could not be saved.");
    }
  };

  const openHistory = () => {
    setRides(getSavedRides());
    setSelectedRide(null);
    setHistoryError("");
    setHistoryOpen(true);
  };

  const removeSelectedRide = () => {
    if (!selectedRide) return false;
    try {
      const removed = deleteRide(selectedRide.id);
      if (!removed) {
        setHistoryError("This ride is no longer in local storage.");
        setRides(getSavedRides());
        return false;
      }
      setRides(getSavedRides());
      setSelectedRide(null);
      setHistoryError("");
      return true;
    } catch (error) {
      setHistoryError(error.message || "Ride could not be deleted.");
      return false;
    }
  };

  const saveSelectedRideNotes = (rideId, notes) => {
    try {
      if (!updateRideNotes(rideId, notes)) {
        throw new Error("Ride notes could not be saved.");
      }
      setRides(getSavedRides());
      setHistoryError("");
      return true;
    } catch {
      setHistoryError("Ride notes could not be saved.");
      setNotice("Ride notes could not be saved.");
      return false;
    }
  };

  const lap = () => {
    const sampledAt = getTimerTime();
    setNow(sampledAt);
    const updatedTimer = recordLap(timer, sampledAt);
    setTimer(updatedTimer);

    if (
      timer.settings.pursuitMode &&
      updatedTimer.laps.length > timer.laps.length
    ) {
      const splitMs = updatedTimer.laps.at(-1).splitMs;
      const lap = updatedTimer.laps.at(-1);
      const roundedSplitMs = Math.round(splitMs);
      const totalTenths = Math.floor(roundedSplitMs / 100);
      const totalSeconds = Math.floor(totalTenths / 10);
      setPursuitLapOverlay({
        id: ++lapOverlaySequence.current,
        splitMs,
        pace: getPursuitPace(lap, timer.settings),
        fading: false,
        display: {
          phase: "stopwatch",
          minutes:
            totalSeconds >= 60
              ? String(Math.floor(totalSeconds / 60)).padStart(2, "0")
              : null,
          seconds: String(totalSeconds % 60).padStart(2, "0"),
          tenths: String(totalTenths % 10),
        },
      });
    }
  };

  const stop = () => {
    const sampledAt = getTimerTime();
    setNow(sampledAt);
    stopScheduledAudio();
    setTimer((current) => stopTimer(current, sampledAt));
  };

  return (
    <main className="app-shell">
      <header className="app-header">
        <div className="brand-lockup">
          <h1>VeloTimer</h1>
          <ActionIcon
            type="button"
            aria-label="Open VeloTimer information"
            title="Information"
            className="settings-trigger info-trigger"
            color="white"
            onClick={() => setInfoOpen(true)}
            size={42}
            variant="subtle"
          >
            <Info aria-hidden="true" />
          </ActionIcon>
        </div>
        <div className="header-actions">
          <ActionIcon
            type="button"
            aria-label="Open ride history"
            title="Ride history"
            className="settings-trigger"
            color="white"
            onClick={openHistory}
            size={42}
            variant="subtle"
          >
            <ClipboardList aria-hidden="true" />
          </ActionIcon>
          <ActionIcon
            type="button"
            aria-label="Open settings"
            title="Settings"
            className="settings-trigger"
            color="white"
            disabled={timer.phase !== "ready" || starting}
            onClick={() => setSettingsOpen(true)}
            size={42}
            variant="subtle"
          >
            <Settings2 aria-hidden="true" />
          </ActionIcon>
        </div>
      </header>

      {isApplyingUpdate && (
        <p className="update-progress" role="status">
          <RefreshCw size={14} aria-hidden="true" />
          App update in progress
        </p>
      )}

      <section className="timer-stage" aria-label="Timer">
        <div className="clock-frame">
          <ClockDigits display={display} fontReady={displayFontReady} />
          <output className="visually-hidden" aria-live="off">
            {formatAnnouncement(display)}
          </output>
        </div>
      </section>

      <p className="status-notice" role="status">
        {notice}
      </p>

      <LapTable
        laps={timer.laps}
        pursuitSettings={timer.settings.pursuitMode ? timer.settings : null}
        scrollRef={lapScrollRef}
        visible={timer.phase === "ended" || timer.laps.length > 0}
      />

      <footer className={`action-dock phase-${timer.phase}`}>
        {showTimingDiagnostics && (
          <TimingDiagnostics
            frameMetrics={frameMetrics}
            fontReady={displayFontReady}
            settings={timer.settings}
          />
        )}
        {timer.phase === "ready" && (
          <Button
            type="button"
            className="action-button action-primary"
            disabled={starting || !displayFontReady}
            loading={starting}
            fullWidth
            onClick={start}
          >
            Start
          </Button>
        )}
        {timer.phase === "countdown" && (
          <Button
            type="button"
            className="action-button action-primary"
            fullWidth
            leftSection={<RotateCcw aria-hidden="true" />}
            onClick={reset}
          >
            Reset
          </Button>
        )}
        {timer.phase === "stopwatch" && (
          <div className="split-actions">
            <Button
              type="button"
              className="action-button action-lap"
              fullWidth
              onClick={lap}
            >
              Lap
            </Button>
            <Button
              type="button"
              className="action-button action-stop"
              fullWidth
              leftSection={<Square aria-hidden="true" fill="currentColor" />}
              onClick={stop}
            >
              Stop
            </Button>
          </div>
        )}
        {timer.phase === "ended" && (
          <div className="end-actions">
            <Button
              type="button"
              className="action-button action-primary"
              disabled={rideSaved}
              leftSection={<ClipboardPen aria-hidden="true" />}
              onClick={openSaveDialog}
            >
              {rideSaved ? "Saved" : "Save"}
            </Button>
            <Button
              type="button"
              className="action-button action-reset"
              leftSection={<RotateCcw aria-hidden="true" />}
              onClick={reset}
            >
              Reset
            </Button>
          </div>
        )}
      </footer>

      {pursuitLapOverlay && (
        <div
          className={`pursuit-lap-overlay pace-${pursuitLapOverlay.pace} ${pursuitLapOverlay.fading ? "is-fading" : ""}`}
          role="status"
          aria-live="polite"
        >
          <div className="pursuit-lap-clock" aria-hidden="true">
            <ClockDigits
              display={pursuitLapOverlay.display}
              fontReady={displayFontReady}
            />
          </div>
          <span className="visually-hidden">
            {pursuitLapOverlay.pace === "ahead"
              ? "Ahead of schedule. "
              : pursuitLapOverlay.pace === "behind"
                ? "Behind schedule. "
                : ""}
            Lap split {formatLapTime(pursuitLapOverlay.splitMs)} seconds
          </span>
        </div>
      )}

      <Modal
        centered
        classNames={{
          content: "save-modal-content",
          header: "save-modal-header",
          title: "save-modal-title",
          body: "save-modal-body",
        }}
        onClose={() => setSaveOpen(false)}
        opened={saveOpen}
        overlayProps={{ backgroundOpacity: 0.7, blur: 2 }}
        title="Save ride"
      >
        <form className="save-form" onSubmit={submitRide}>
          <Autocomplete
            autoComplete="off"
            classNames={{ input: "save-input", label: "save-label" }}
            data={getSavedRiderNames()}
            label="Rider Name"
            maxDropdownHeight={220}
            required
            value={riderName}
            onChange={setRiderName}
          />
          <NumberInput
            allowDecimal={false}
            allowNegative={false}
            classNames={{ input: "save-input", label: "save-label" }}
            clampBehavior="strict"
            label="Distance"
            min={0}
            required
            suffix=" m"
            thousandSeparator=","
            value={distanceMeters}
            onChange={setDistanceMeters}
          />
          {saveError && (
            <p className="ride-form-error" role="alert">
              {saveError}
            </p>
          )}
          <div className="save-form-actions">
            <Button onClick={() => setSaveOpen(false)} variant="default">
              Cancel
            </Button>
            <Button
              leftSection={<ClipboardPen aria-hidden="true" />}
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <SettingsDialog
        settings={timer.settings}
        onSettingChange={changeSettings}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <Drawer
        classNames={{
          content: "info-drawer-content",
          header: "info-drawer-header",
          title: "info-drawer-title",
          body: "info-drawer-body",
        }}
        closeButtonProps={{
          "aria-label": "Close VeloTimer information",
          icon: <X aria-hidden="true" />,
        }}
        onClose={() => setInfoOpen(false)}
        opened={infoOpen}
        overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
        position="left"
        size="min(90vw, 420px)"
        title="About VeloTimer"
      >
        <div className="info-drawer-markdown">
          <InfoContent />
        </div>
        <footer className="info-drawer-credits">
          <p>
            Fonts used under SIL Open Font License 1.1 &bull; DSEG © 2017
            keshikan &bull; Atkinson Hyperlegible Mono © 2020-2024 The Atkinson
            Hyperlegible Mono Project Authors
          </p>
        </footer>
      </Drawer>
      <SavedRidesDrawer
        rides={rides}
        selectedRide={selectedRide}
        onSelect={setSelectedRide}
        onBack={() => setSelectedRide(null)}
        onDelete={removeSelectedRide}
        onSaveNotes={saveSelectedRideNotes}
        error={historyError}
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setSelectedRide(null);
        }}
      />
    </main>
  );
}

const theme = createTheme({
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  primaryColor: "red",
});

createRoot(document.getElementById("root")).render(
  <MantineProvider forceColorScheme="dark" theme={theme}>
    <TimerApp />
  </MantineProvider>,
);
